/* eslint-disable max-lines */
import { PageId } from 'page-constants'
import { TAppName, TStepsVariant, TTestSegment, TValueOf } from 'root-types'

export const AppName = {
  blessedPath: 'blessedpath',
  ayahPath: 'ayahpath',
  karmaPath: 'karmapath',
} as const

export const enum CurrentEnvironment {
  DEV = 'dev',
  STAGE = 'stage',
  PROD = 'prod',
}

export const enum RequestMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  HEAD = 'HEAD',
  OPTIONS = 'OPTIONS',
  PATCH = 'PATCH',
}

export const enum MediaBreakpoint {
  MIN_PHONE = 321,
  MEDIUM_PHONE = 360,
  MAX_PHONE = 415,
  TABLET = 520,
  LAPTOP = 1200,
}

export const INITIAL_TIMER_VALUES = {
  TEN_MINUTES: { minutes: '10', seconds: '00' },
  FIVE_MINUTES: { minutes: '05', seconds: '00' },
}

export const MINUTES_IN_SECONDS = {
  FIFTEEN: 900,
  TEN: 600,
  FIVE: 300,
  ONE: 60,
  ZERO: 0,
}

export const SECONDS_IN_MINUTE = 60

export const MILLISECONDS_IN_SECOND = 1000

export const TIMER_DIGIT_LENGTH = 2

export const enum CardPaymentFieldName {
  NAME = 'name',
  NUMBER = 'cardNumber',
  EXPIRY = 'cardExpiry',
  CVC = 'cardCvc',
}

export const AppLinks = {
  TOU_LINK: `/${PageId.TERMS_OF_USE}`,
  PP_LINK: `/${PageId.PRIVACY_POLICY}`,
  COOKIE_LINK: `/${PageId.COOKIE_POLICY}`,
} as const

export const TOU_PP_COOKIE_LINKS: string[] = [
  AppLinks.TOU_LINK,
  AppLinks.PP_LINK,
  AppLinks.COOKIE_LINK,
]

export const HEADER_HEIGHT = 48
export const SECTIONED_PROGRESS_HEADER_HEIGHT = 54

export const APP_OUTER_LINKS = {
  [AppName.ayahPath]:
    'https://ayahpath.onelink.me/tdem?af_xp=custom&pid=web2web_payment_success',
  [AppName.blessedPath]: 'https://blessedpaths.onelink.me/dkdK/yo3sq7k5',
  [AppName.karmaPath]:
    'https://karmapath.onelink.me/jwTw?af_xp=custom&pid=web2web',
} as const satisfies Record<TAppName, string>

export const EMAIL_REGEXP =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3})|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
export const ALPHABETIC_SYMBOLS_REGEXP = /[^\p{L}\p{Z}]+/gu

export const MIN_PASSWORD_LENGTH = 8

export const CDN_FOLDER_LINK =
  'https://cdn-prod-unified-content.gismart.xyz/blessedpath'
export const IMAGES_PATH = '/images'
export const ANIMATIONS_PATH = '/animations'

export const APPLOVIN_ARTS_AND_ENTERTAINMENT_CATEGORY_ID = 8

export const enum Color {
  PRIMARY = '#D9945A',
  ERROR = '#ea5873',
  WHITE = '#fff',
  DARK = '#2d3240',
  DARK_SECONDARY = '#2c3131',
  NAVY_BLUE = '#1E353E',
  LIGHT = '#81848c',
  BLUE_DARK = '#17202a',
  BLUE_GRAY = '#4C4C4C',
  MIDNIGHT_BLUE = '#7590CE',
  GRAY = '#626262',
  CORAL_GRAY = '#f2f3f5',
  GRAY_SECONDARY = '#aaacb2',
  GRAY_LIGHT = '#e9e9e9',
  GRAY_LIGHTEST = '#F2F4F5',
  GRAY_LIGHTER = '#9498A5',
  RED = '#e85e47',
  RED_LIGHTEST = '#FFE5E5',
  RED_DARKEST = '#D3180C',
  RED_BASE = '#FF5247',
  YELLOW = '#FFB600',
  BLACK = '#000000',
  SAND_BASE = '#E6E1D1',
  SAND_DARK = '#CDC5A9',
  SAND_PALE = '#E9E6DF',
  SAND_LIGHTER = '#EBE8E0',
  SAND_LIGHT = '#F3EFE6',
  SAND_LIGHTEST = '#F4F1EC',
  SAND_WHITE = '#FBF7EE',
  SAND_GRAY = '#D8D5D0',
  SKY = '#E7ECEF',
  SKY_BASE = '#D7DFE5',
  SKY_LIGHT = '#DFE5EA',
  SKY_LIGHTER = '#F7F9FA',
  SKY_DARK = '#979CA0',
  INK_DARKER = '#202325',
  INK_DARKEST = '#090A0A',
  INK_LIGHT = '#6C7072',
  GOLD_BASE = '#AD903C',
  GOLD_MEDIUM = '#9F8744',
  GOLD_LIGHT = '#D7B555',
  GOLD_LIGHTER = '#FFD188',
  GOLD_LIGHTEST = '#FCF0D0',
  GOLD_DARKEST = '#7B6215',
  GOLD_CHAMPAGNE = '#FFE9BF',
  GOLD_PASTEL = '#F3DCAE',
  GOLD = '#EFD081',
  INK_LIGHTER = '#8C9093',
  INK_DARK = '#303437',
  INK_BASE = '#404446',
  GREEN_DARKEST = '#198155',
  GREEN_LIGHT = '#B5BF7B',
  GREEN_LIGHTER = '#ECFCE5',
  GREEN_PASTEL = '#BDDAC8',
  GREEN_PALE = '#D6EBD3',
  BLUE_DARKER = '#0065D0',
  BLUE_LIGHTER = '#48A7F8',
  BLUE_SAGE = '#518DCC',
  BLUE_LIGHTEST = '#C9F0FF',
  BLUE_PASTEL = '#C9D9EB',
  TEAL_GRAY = '#567B83',
  TEAL_LIGHT = '#7DB0BD',
  BEIGE_BASE = '#B2A190',
  PINK_BASE = '#ffefec',
  PINK_LIGHT = '#FFF4F4',
  PINK_DARK = '#DEB8A7',
  PINK_PASTEL = '#E6C2DA',
}

export const enum TimeInterval {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export const enum EventLoggerInstanceName {
  GIA = 'gia-amplitude',
  AMPLITUDE = 'amplitude',
  USER_FLOW_TRACKER = 'user-flow-tracker',
}

export const enum PlatformOS {
  ANDROID = 'Android',
  IOS = 'iOS',
  UNKNOWN = 'unknown',
}

export const enum AppWithInAppBrowser {
  FACEBOOK = 'facebook',
  INSTAGRAM = 'instagram',
}

export const enum Cohort {
  FLOW_1_INTRO_LONG_2248 = 'flow1_intro_long_2248',
  FLOW_1_INTRO_LONG_2466 = 'flow1_intro_long_2466',
  FLOW_1_INTRO_LONG_2467 = 'flow1_intro_long_2467',
  FLOW_1_INTRO_LONG_2251 = 'flow1_intro_long_2251',
  FLOW_1_INTRO_LONG_2252 = 'flow1_intro_long_2252',
  FLOW_1_INTRO_LONG_2253 = 'flow1_intro_long_2253',
  FLOW_1_INTRO_LONG_4412 = 'flow1_intro_long_4412',
  FLOW_1_INTRO_LONG_2261 = 'flow1_intro_long_2261',
  FLOW_1_INTRO_LONG_3350 = 'flow1_intro_long_3350',
  FLOW_1_INTRO_LONG_2468 = 'flow1_intro_long_2468',
  FLOW_1_INTRO_LONG_2469 = 'flow1_intro_long_2469',
  FLOW_1_INTRO_LONG_2271 = 'flow1_intro_long_2271',
  FLOW_1_INTRO_LONG_2271_AM = 'flow1_intro_long_2271_am',
  FLOW_1_INTRO_LONG_2271_C = 'flow1_intro_long_2271_c',
  BP_HEALING_2433 = 'bp_healing_2433',
  FLOW_AYAH_313 = 'flow_ayah_313',
  FLOW_AYAH_314 = 'flow_ayah_314',
  FLOW_AYAH_314_C = 'flow_ayah_314_c',
  AP_QURAN_313_C = 'ap_quran_313_c',
  AP_QURAN_313_C_AM = 'ap_quran_313_c_am',
  FLOW_AYAH_313_AM = 'flow_ayah_313_am',
  FINAL_MONTHLY_AYAH_AM = 'final_monthly_ayah_am',
  AP_QURAN_313_CT = 'ap_quran_313_ct',
  AP_QURAN_313_CC = 'ap_quran_313_cc',
  AP_QURAN_313_C_V2 = 'ap_quran_313_c_v2',
  AP_QURAN_313_CF = 'ap_quran_313_cf',
  AP_WLDB_313 = 'ap_wldb_313',
  AP_WLDB_QUIZ_313 = 'ap_wldb_quiz_313',
  AP_PA_440 = 'ap_pa_440',
  AP_PA_440_AM = 'ap_pa_440_am',
  AP_PA_440_C = 'ap_pa_440_c',
  AP_PA_440_C_AM = 'ap_pa_440_c_am',
  AP_QURAN_315 = 'ap_quran_315',
  AP_PRINTABLE_520 = 'ap_printable_520',
  BP_STABLE_INTRO = 'bp_stable_intro',
  AP_STABLE_INTRO = 'ap_stable_intro',
  KP_STABLE_INTRO = 'kp_stable_intro',
  FINAL_MONTHLY = 'final_monthly',
  FINAL_MONTHLY_AM = 'final_monthly_am',
  FINAL_MONTHLY_AYAH = 'final_monthly_ayah',
  FINAL_MONTHLY_KARMA = 'final_monthly_karma',
  FINAL_MONTHLY_KARMA_AM = 'final_monthly_karma_am',
  G_6007 = 'g_6007',
  KP_BS_13 = 'kp_bs_13',
  KP_BS_13_AM = 'kp_bs_13_am',
  KP_BS_13_C = 'kp_bs_13_c',
  KP_BS_13_CT = 'kp_bs_13_ct',
  KP_BS_13_CC = 'kp_bs_13_cc',
  KP_BS_7_WTYPE_C = 'kp_bs_7_wtype_c',
  KP_BS_7_WTYPE = 'kp_bs_7_wtype',
  KP_BS_7_TCHMNTR_C = 'kp_bs_7_tchmntr_c',
  KP_BS_13_H = 'kp_bs_13_h',
  AP_QURAN_326 = 'ap_quran_326',
  AP_QURAN_337 = 'ap_quran_337',
  BP_BIBLE_2284 = 'bp_bible_2284',
  AP_QURAN_610 = 'ap_quran_610',
  AP_QURAN_410 = 'ap_quran_410',
  AP_FINANCE_313_C = 'ap_finance_313_c',
  AP_FINANCE_313 = 'ap_finance_313',
  AP_FINANCE_313_C_AM = 'ap_finance_313_c_am',
  AP_FINANCE_313_AM = 'ap_finance_313_am',
  AP_SD_313_C = 'ap_sd_313_c',
  AP_SD_313_CC = 'ap_sd_313_cc',
  AP_PARENTING_313_C = 'ap_parenting_313_c',
  AP_KNOWLEDGE_428_C = 'ap_knowledge_428_c',
}

export const FirebaseSignUpErrorCode = {
  EMAIL_EXISTS: 'auth/email-already-in-use',
} as const

export const AyahPathCohorts = [
  Cohort.FLOW_AYAH_313,
  Cohort.FLOW_AYAH_313_AM,
  Cohort.FLOW_AYAH_314,
  Cohort.FLOW_AYAH_314_C,
  Cohort.FINAL_MONTHLY_AYAH,
  Cohort.FINAL_MONTHLY_AYAH_AM,
  Cohort.AP_PA_440,
  Cohort.AP_PA_440_AM,
  Cohort.AP_PA_440_C,
  Cohort.AP_PA_440_C_AM,
  Cohort.AP_QURAN_315,
  Cohort.AP_PRINTABLE_520,
  Cohort.AP_QURAN_326,
  Cohort.AP_QURAN_337,
  Cohort.AP_QURAN_610,
  Cohort.AP_QURAN_410,
  Cohort.AP_QURAN_313_C,
  Cohort.AP_QURAN_313_C_AM,
  Cohort.AP_QURAN_313_CT,
  Cohort.AP_QURAN_313_CF,
  Cohort.AP_WLDB_313,
  Cohort.AP_WLDB_QUIZ_313,
] as readonly Cohort[]

export const enum PagePath {
  LOCALHOST = 'localhost',
  SUBSCRIPTIONS = 'subscriptions',
}

export const enum SubscriptionListType {
  PURCHASE = 'purchase',
  UPGRADE = 'upgrade',
  UPSELL = 'upsell',
  ONE_TIME = 'one_time',
}

export const enum SubscriptionTagsType {
  FULL_OFFER = 'full_offer',
  NO_TAX = 'no_tax',
  NO_TAX_OLD = 'no_tax_old',
  NO_TAX_NEW_PRICE = 'no_tax_newprice',
  NO_TAX_NEW_PRICE_2 = 'no_tax_newprice_2',
  NO_TAX_BILLING_PERIOD = 'no_tax_billing_period',
  NO_TAX_US_GEO = 'no_tax_us_geo',
  NEW_PRICE_V1_B = 'new_price_v1_b',
  NEW_PRICE_V1_C = 'new_price_v1_c',
  NEW_PRICE_V1_D = 'new_price_v1_d',
  NEW_PRICE_V3 = 'new_price_v3',
  NEW_PRICING = 'new_pricing',
  ROUNDING = 'rounding',
  UPI_TEST = 'upi_test',
  CANCEL_OFFER = 'cancel',
  PODCAST = 'podcast',
  PA = 'pa',
  PDF_FINDING = 'pdf_finding',
  PDF_HOW_TO = 'pdf_how_to',
  VOICEOVER = 'voiceover',
  VOICEOVER_V1 = 'voiceover_v1',
  VOICEOVER_V2 = 'voiceover_v2',
  GUIDE_WELLNESS = 'guide_wellness',
  PHYSICAL_WELLNESS = 'physical_wellness',
}

export const OptionType = {
  RADIO: 'radio',
  CHECKBOX: 'checkbox',
} as const

export const BUTTON_TEXT = {
  NO: 'no_thank_you',
  GOT_IT: 'got_it',
  ADD_ONE_TIME_PAYMENT: 'add_to_my_program',
  WITHOUT_OFFER: 'continue_without_special_offer',
  BUY: 'buy',
  CONTINUE: 'continue',
  SKIP: 'skip',
}

export const CENTS_IN_DOLLAR = 100
export const INITIAL_PRICE = 0
export const FREE_TRIAL_PRICE = 0
export const DEFAULT_CURRENCY = 'usd'
export const TEST_ENV_QUERY_PARAM = 'is_test_environment'
export const LANG_QUERY_PARAM = 'lang'
export const KEYWORD_PARAM = 'lp_dynamic'
export const COHORT_QUERY_PARAM = 'cohort'
export const AD_NAME_QUERY_PARAM = 'ad_name'
export const PINTEREST_CLICK_ID_QUERY_PARAM = 'epik'
export const FUNNEL_THEME_QUERY_PARAM = 'funnel_theme'
export const HEADLINE_QUERY_PARAM = 'headline'
export const SUBTEXT_QUERY_PARAM = 'subtext'
export const PLAN_QUERY_PARAM = 'plan'

export const FunnelTheme = {
  ADDICT: 'addict',
  GENERAL: 'general',
  FINANCE: 'finance',
} as const

export const enum BillingCycle {
  ONE_WEEK = 7,
  ONE_MONTH = 1,
  THREE_MONTHS = 3,
  SIX_MONTHS = 6,
  NINE_MONTHS = 9,
  TWELVE_MONTHS = 12,
}

export const enum SubscriptionProduct {
  THREE_DAY = 3,
  SEVEN_DAY = 7,
  ONE_MONTH = 30,
  THREE_MONTHS = 90,
  SIX_MONTHS = 180,
}

export const enum ScreenName {
  PURCHASE = 'onboarding',
  CHECKOUT = 'checkout',
  CANCEL_OFFER = 'cancel_offer',
  UPSELL_OFFER = 'upsell_offer',
  INAPP_OFFER = 'inapp_offer',
  CANCEL_OFFER_UPSELL = 'cancel_offer_upsell',
  INAPP_HOW_TO = 'inapp_how_to',
  INAPP_FINDING = 'inapp_relive_stress',
  INAPP_VOICEOVER = 'inapp_voiceover',
  LANDING_WELLNESS = 'landing_wellness',
}

export const enum Language {
  EN = 'en',
  AR = 'ar',
  FR = 'fr',
  ES = 'es',
  DE = 'de',
  PT = 'pt',
  TR = 'tr',
  ID = 'id',
  IN_CONTEXT = 'ach',
}

export const INITIAL_LANGUAGE = Language.EN

export const ALL_SUPPORTED_LOCALES: string[] = [
  Language.EN,
  Language.AR,
  Language.FR,
  Language.ES,
  Language.DE,
  Language.PT,
  Language.TR,
  Language.ID,
]

export const SUPPORTED_LOCALES_MAP: Partial<Record<Cohort, string[]>> = {
  // AyahPath cohorts
  [Cohort.AP_WLDB_313]: [Language.EN],
  [Cohort.AP_WLDB_QUIZ_313]: [Language.EN],
  [Cohort.FLOW_AYAH_313]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
    Language.ID,
    Language.TR,
  ],
  [Cohort.FLOW_AYAH_313_AM]: [Language.EN],
  [Cohort.AP_QURAN_313_C]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
    Language.TR,
    Language.ID,
  ],
  [Cohort.AP_QURAN_313_CF]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
    Language.TR,
    Language.ID,
  ],
  [Cohort.AP_QURAN_313_C_AM]: [Language.EN],
  [Cohort.AP_QURAN_313_CC]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
    Language.TR,
  ],
  [Cohort.AP_QURAN_313_CT]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.DE,
  ],
  [Cohort.AP_FINANCE_313_C]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.TR,
    Language.ID,
  ],
  [Cohort.AP_FINANCE_313]: [
    Language.EN,
    Language.AR,
    Language.FR,
    Language.TR,
    Language.ID,
  ],
  [Cohort.AP_FINANCE_313_C_AM]: [Language.EN],
  [Cohort.AP_FINANCE_313_AM]: [Language.EN],
  [Cohort.FINAL_MONTHLY_AYAH]: [
    Language.EN,
    Language.TR,
    Language.FR,
    Language.AR,
    Language.DE,
    Language.ID,
  ],
  [Cohort.AP_PARENTING_313_C]: [
    Language.EN,
    Language.TR,
    Language.AR,
    Language.FR,
  ],
  [Cohort.FINAL_MONTHLY_AYAH_AM]: [Language.EN],
  // BlessedPath cohorts
  [Cohort.FLOW_1_INTRO_LONG_2271]: [Language.EN, Language.ES],
  [Cohort.FLOW_1_INTRO_LONG_2271_AM]: [Language.EN],
  [Cohort.FLOW_1_INTRO_LONG_2271_C]: [Language.EN, Language.ES],
  // KarmaPath cohorts
  [Cohort.KP_BS_13]: [Language.EN, Language.ES, Language.FR, Language.PT],
  [Cohort.KP_BS_13_AM]: [Language.EN],
  [Cohort.KP_BS_13_C]: [Language.EN, Language.ES, Language.FR, Language.PT],
  [Cohort.KP_BS_7_WTYPE]: [Language.EN, Language.ES, Language.FR, Language.PT],
  [Cohort.KP_BS_7_WTYPE_C]: [
    Language.EN,
    Language.ES,
    Language.FR,
    Language.PT,
  ],
}

export const VAT_INCLUDED_COUNTRY = ['US', 'CA', 'AU']

export const CALIFORNIA_STATE_CODE = 'CA'

export const EU_COUNTRY_CODES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'GB',
  'NO',
  'IS',
  'CH',
  'LI',
]

export const US_STATES_WITH_COOKIE_CONSENT = [
  'CA',
  'CT',
  'CO',
  'VA',
  'NV',
  'UT',
  'AZ',
  'DE',
  'IA',
  'IL',
  'FL',
  'ME',
  'MD',
  'MN',
  'MT',
  'NE',
  'NY',
  'OR',
  'SC',
  'TX',
]

export const COOKIE_CONSENT_LANGUAGES: string[] = [
  Language.EN,
  Language.FR,
  Language.DE,
  Language.AR,
  Language.TR,
  Language.ID,
]

export const COOKIE_CONSENT_COHORTS: Cohort[] = [
  Cohort.AP_QURAN_313_C,
  Cohort.AP_QURAN_313_CF,
  Cohort.AP_FINANCE_313_C,
  Cohort.AP_PA_440_C,
  Cohort.FINAL_MONTHLY_AYAH,
  Cohort.AP_FINANCE_313,
  Cohort.AP_QURAN_313_C_AM,
  Cohort.AP_FINANCE_313_C_AM,
  Cohort.AP_PA_440_AM,
  Cohort.AP_PA_440_C_AM,
  Cohort.AP_FINANCE_313_AM,
  Cohort.FINAL_MONTHLY_AYAH_AM,
  Cohort.FLOW_AYAH_313_AM,
  Cohort.FLOW_AYAH_313,
  Cohort.FINAL_MONTHLY_AM,
  Cohort.FINAL_MONTHLY_KARMA_AM,
  Cohort.FLOW_1_INTRO_LONG_2271_AM,
  Cohort.KP_BS_13_AM,
  Cohort.AP_SD_313_C,
  Cohort.AP_SD_313_CC,
  Cohort.AP_PARENTING_313_C,
  Cohort.FLOW_AYAH_314,
  Cohort.FLOW_AYAH_314_C,
  Cohort.AP_KNOWLEDGE_428_C,
]

export const enum CountryCode {
  AFGHANISTAN = 'af',
  ALBANIA = 'al',
  ALGERIA = 'dz',
  ANGOLA = 'ao',
  ARGENTINA = 'ar',
  AZERBAIJAN = 'az',
  BAHRAIN = 'bh',
  BANGLADESH = 'bd',
  BELIZE = 'bz',
  BENIN = 'bj',
  BOLIVIA = 'bo',
  BRAZIL = 'br',
  BULGARIA = 'bg',
  BURKINA_FASO = 'bf',
  BURUNDI = 'bi',
  CAMBODIA = 'kh',
  CAMEROON = 'cm',
  CAYMAN_ISLANDS = 'ky',
  CENTRAL_AFRICAN_REPUBLIC = 'cf',
  CHAD = 'td',
  CHILE = 'cl',
  CHINA = 'cn',
  COLOMBIA = 'co',
  COMOROS = 'km',
  CONGO = 'cg',
  CONGO_DRC = 'cd',
  COSTA_RICA = 'cr',
  CURACAO = 'cw',
  CYPRUS = 'cy',
  CZECH_REPUBLIC = 'cz',
  DJIBOUTI = 'dj',
  DOMINICA = 'dm',
  DOMINICAN_REPUBLIC = 'do',
  ECUADOR = 'ec',
  EGYPT = 'eg',
  EL_SALVADOR = 'sv',
  ESTONIA = 'ee',
  ETHIOPIA = 'et',
  FIJI = 'fj',
  GABON = 'ga',
  GAMBIA = 'gm',
  GEORGIA = 'ge',
  GHANA = 'gh',
  GUATEMALA = 'gt',
  GUINEA = 'gn',
  GUINEA_BISSAU = 'gw',
  GUYANA = 'gy',
  HAITI = 'ht',
  HONDURAS = 'hn',
  HONG_KONG = 'hk',
  HUNGARY = 'hu',
  ICELAND = 'is',
  INDIA = 'in',
  INDONESIA = 'id',
  IRAQ = 'iq',
  IVORY_COAST = 'ci',
  JAMAICA = 'jm',
  JORDAN = 'jo',
  KAZAKHSTAN = 'kz',
  KENYA = 'ke',
  KIRIBATI = 'ki',
  KOSOVO = 'xk',
  KUWAIT = 'kw',
  KYRGYZSTAN = 'kg',
  LAOS = 'la',
  LATVIA = 'lv',
  LEBANON = 'lb',
  LESOTHO = 'ls',
  LIBERIA = 'lr',
  LIBYA = 'ly',
  LITHUANIA = 'lt',
  MADAGASCAR = 'mg',
  MALAWI = 'mw',
  MALAYSIA = 'my',
  MALDIVES = 'mv',
  MALI = 'ml',
  MALTA = 'mt',
  MARTINIQUE = 'mq',
  MAURITANIA = 'mr',
  MAYOTTE = 'yt',
  MEXICO = 'mx',
  MOLDOVA = 'md',
  MONTENEGRO = 'me',
  MOROCCO = 'ma',
  MYANMAR = 'mm',
  NAMIBIA = 'na',
  NEPAL = 'np',
  NICARAGUA = 'ni',
  NIGER = 'ne',
  NIGERIA = 'ng',
  OMAN = 'om',
  PAKISTAN = 'pk',
  PALESTINE = 'ps',
  PANAMA = 'pa',
  PAPUA_NEW_GUINEA = 'pg',
  PARAGUAY = 'py',
  PERU = 'pe',
  PHILIPPINES = 'ph',
  POLAND = 'pl',
  PUERTO_RICO = 'pr',
  ROMANIA = 'ro',
  RWANDA = 'rw',
  SAINT_LUCIA = 'lc',
  SAUDI_ARABIA = 'sa',
  SENEGAL = 'sn',
  SERBIA = 'rs',
  SEYCHELLES = 'sc',
  SIERRA_LEONE = 'sl',
  SINGAPORE = 'sg',
  SOMALIA = 'so',
  SOUTH_AFRICA = 'za',
  SOUTH_KOREA = 'kr',
  SOUTH_SUDAN = 'ss',
  SRI_LANKA = 'lk',
  SUDAN = 'sd',
  SWAZILAND = 'sz',
  SYRIA = 'sy',
  TAIWAN = 'tw',
  TAJIKISTAN = 'tj',
  TANZANIA = 'tz',
  THAILAND = 'th',
  TOGO = 'tg',
  TONGA = 'to',
  TUNISIA = 'tn',
  TURKEY = 'tr',
  UGANDA = 'ug',
  UKRAINE = 'ua',
  UNITED_ARAB_EMIRATES = 'ae',
  UNITED_STATES = 'us',
  URUGUAY = 'uy',
  UZBEKISTAN = 'uz',
  VENEZUELA = 've',
  VIETNAM = 'vn',
  YEMEN = 'ye',
  ZAMBIA = 'zm',
  ZIMBABWE = 'zw',
}

export const enum CookieConsentOption {
  STRICTLY_NECESSARY = 'necessary',
  ANALYTICS_AND_PERFORMANCE = 'analytics',
  ADVERTISING_AND_TARGETING = 'advertising',
  FUNCTIONAL_COOKIES = 'functional_cookies',
}

export const TRIAL_BILLING_CYCLE = {
  SEVEN_DAYS: 7,
}

export const enum Gender {
  MALE = 'male',
  FEMALE = 'female',
}

export const enum PrintableGoal {
  PRAYER_HABIT = 'prayer_habit',
  RELIEVE_STRESS = 'relieve_stress',
  QURAN_HABIT = 'build_quran_habit',
  STRENGTHEN_SUNNAH_ROUTINE = 'strengthen_sunnah_routine',
  BOTH = 'both_options',
}

export const enum UserAnswer {
  YES = 'yes',
  NO = 'no',
  AGREE = 'agree',
  DISAGREE = 'disagree',
  HARAM = 'haram',
  HALAL = 'halal',
}

export const BUNDLE_IDENTIFIER = 'bundle'

export const enum ProductKey {
  UPSELL_BUNDLE = 'guide_bundle',
  UPSELL_HOW_TO = 'guide_how_to',
  UPSELL_FINDING = 'guide_finding',
  UPSELL_PODCASTS_BUNDLE = 'podcast_bundle',
  UPSELL_PASTOR_ADAM_PODCAST = 'podcast_pastor_adam',
  UPSELL_PASTOR_NICK_PODCAST = 'podcast_pastor_nick',
  UPSELL_BUNDLE_PA = 'guide_bundle_pa',
  UPSELL_GUIDE_ISLAMIC_FRAMEWORK = 'guide_islamic_framework',
  UPSELL_GUIDE_FIX_TENSION = 'guide_fix_tension',
  UPSELL_KARMA_BUNDLE = 'karma_bundle',
  UPSELL_CALM_TOOLKIT = 'karma_calm_toolkit',
  UPSELL_SKILLFUL_LIFE = 'karma_skillful_life',
  UPSELL_ADDON_BUNDLE = 'addon_bundle',
  UPSELL_ADDON_DAILY_DUA = 'addon_daily_dua',
  UPSELL_REFLECTION_SET = 'addon_reflection_set',
  INAPP_HOW_TO_PDF = 'guide_how_to_pdf',
  INAPP_FINDING = 'guide_stress_relieve',
  INAPP_VOICEOVER = 'otp_voiceover',
  WELLNESS_DESKBOOK = 'otp_wellness_no_key',
}

export const PageVersions = {
  V1: 'v1',
  V2: 'v2',
  V3: 'v3',
  V4: 'v4',
  V5: 'v5',
  V6: 'v6',
  V7: 'v7',
  V8: 'v8',
  V9: 'v9',
} as const

export const Variant = {
  DEFAULT: 'default',
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
} as const

export const HeaderTitle = {
  blessedPath: 'BlessedPath',
  ayahPath: 'AyahPath',
  karmaPath: 'KarmaPath',
  triggers: 'TRIGGERS',
  patterns: 'PATTERNS',
  goals: 'GOALS',
} as const

export const Emoji = {
  sadFace: '😔',
  frustratedFace: '😤',
  thinkingFace: '🤔',
  innocentFace: '😇',
  disappointedFace: '☹️',
  relievedFaceWithTear: '😥',
  angryFace: '😡',
  sneezingFace: '🤧',
  unamusedFace: '😒',
  relievedFace: '😌',
  slightlySmilingFace: '🙂',
  cryingFace: '😢',
  dizzyFace: '🥴',
  anxiousFaceWithSweat: '😰',
  confusedFace: '😕',
  coolFace: '😎',
  yawningFace: '🥱',
  perseveringFace: '😣',
  exhalingFace: '😮‍💨',
  sleepingFace: '😴',
  tiredFaceWithSweat: '😓',
  uneasyFace: '🫤',
  neutralFace: '😐',
  meltingFace: '🫠',
  spiralEyesFace: '😵‍💫',
  flexMuscle: '💪',
  sparklingHeart: '💖',
  revolvingHearts: '💞',
  fullBattery: '🔋',
  lowBattery: '🪫',
  calendar: '📅',
  sleepingAccommodation: '🛌🏾',
  weightLifter: '🏋️',
  workBriefcase: '💼',
  goalTarget: '🎯',
  underageWarning: '🔞',
  hamburgerFood: '🍔',
  clockShelf: '🕰️',
  trophyAward: '🏆',
  growthChart: '📈',
  electricZap: '⚡',
  meditatingMan: '🧘‍♂️',
  foldedHands: '🙏',
  honeyPot: '🍯',
  olive: '🫒',
  seedling: '🌱',
  palmTree: '🌴',
  sheafOfRice: '🌾',
  herb: '🌿',
} as const

export const INITIAL_INPUT_VALUE = {
  value: '',
  isValid: false,
}

export const PAGES_SEGMENTS = {
  VARIANT_A: 'a',
  VARIANT_B: 'b',
  VARIANT_C: 'c',
  VARIANT_D: 'd',
  VARIANT_E: 'e',
} as const

export const TEST_SEGMENTS = {
  VARIANT_A: 'variant_a',
  VARIANT_B: 'variant_b',
  VARIANT_C: 'variant_c',
  VARIANT_D: 'variant_d',
  VARIANT_E: 'variant_e',
} as const

export const TEST_SEGMENT_TO_PAGES_SEGMENT = {
  [TEST_SEGMENTS.VARIANT_A]: PAGES_SEGMENTS.VARIANT_A,
  [TEST_SEGMENTS.VARIANT_B]: PAGES_SEGMENTS.VARIANT_B,
  [TEST_SEGMENTS.VARIANT_C]: PAGES_SEGMENTS.VARIANT_C,
  [TEST_SEGMENTS.VARIANT_D]: PAGES_SEGMENTS.VARIANT_D,
  [TEST_SEGMENTS.VARIANT_E]: PAGES_SEGMENTS.VARIANT_E,
} as const satisfies Record<TTestSegment, TStepsVariant>

export const GROWTHBOOK_FEATURE_KEY = {
  BLESSED_ONBOARDING_2271_TEST: 'bpw_2271_onboarding_test',
  AYAH_313_NEW_QUIZ_TEST: 'apw_4_flow_ayah_313_new_quiz',
  AYAH_313_FITME_PRICES_TEST: 'apw_5_flow_ayah_313_fitme_prices',
  KARMA_13_DUOMO_PAYWALL_TEST: 'kpw_1_kp_bs_13_duomo_paywall_v1',
  BLESSED_2271_C_LOWER_PRICES_TEST: 'bpw_2271_c_lp_4w_test',
  KARMA_13_C_7D_1M_SUB_TEST: 'kpw_9_kp_bs_13_c_7d_vs_1m_sub',
  BLESSED_2271_C_3D_TRIAL_TEST: 'bpw_2271_c_lp_3d_trial',
  BLESSED_2271_2248_MONETIZATION_TEST: 'bpw_2271_2248_monetization',
  KARMA_13_KP_BS_13_C_3D_TRIAL_TEST: 'kpw_13_kp_kb_bs_13_c_3d_trial',
  KARMA_15_WELCOME_PAGE_TRUSTPILOT_TEST: 'kpw_15_kp_kb_bs_13_c_wp_trst_1',
  AYAH_16_AP_QURAN_313_C_CHECKOUT_TEST: 'apw_16_ap_quran_313_c_checkout_modal',
  KARMA_17_KP_BS_13_C_NEW_FORMAT_TEST: 'kpw_17_kp_kb_bs_13_c_new_quest_format',
  KARMA_19_KP_BS_13_C_LOADER_TEST: 'kpw_19_kp_kb_bs_13_c_wp_loader',
  KARMA_27_MULTISELECT_STRUGGLES_TEST: 'kpw_27_kp_kb_bs_13_c_strgls_mltslct',
  KARMA_28_WTYPE_HIDDEN_PROFILE_TEST: 'kpw_28_kp_kb_bs_7_wtype_c_notype',
  KARMA_PRIMER_CARD_ACTIVE_TEST: 'kpw_21_primer_card',
  AYAH_PRIMER_CARD_ACTIVE_TEST: 'apw_17_primer_card',
  BLESSED_PRIMER_CARD_ACTIVE_TEST: 'bpw_primer_card',
  KARMA_34_PAYPAL_PRIORITY_TEST: 'kpw_34_pp_prio',
  KARMA_36_INTRO_PRICE_SELECT_TEST: 'kpw_37_kp_bs_13_c_intropriceselect',
  AYAH_39_1_SCREEN_WORDING: 'apw_39_ap_pa_440_c_1scrn_wording',
  AYAH_42_FLOW_AYAH_314_1SCRN_LOADER: 'apw_42_flow_ayah_314_1scrn_loader',
  AYAH_44_BREAK_SCREEN: 'apw_44_ap_quran_313_c_break_scrn',
  AYAH_46_STICKY_PAYMENTS: 'apw_46_ap_quran_313_c_sticky_payments',
  AYAH_47_PRELOAD_SCREEN_TEST: 'apw_47_ap_auran_313_c_preload_screens',
  AYAH_55_28_DAYS_TEST: 'apw_55_28d',
  AYAH_56_3_MONTH_SUB_TEST: 'apw_56_3msub_1mtrial',
  AYAH_58_REDIRECT_TO_LW_TEST: 'apw_58_redirect_to_lw',
  AYAH_61_PW_SPEED_TEST: 'apw_61_ap_quran_313_c_pwspeed',
  AYAH_48_ADDITIONAL_DISCOUNT_TEST:
    'apw_48_ap_quran_313_c_additional_discount_v2',
  AYAH_49_FACEBOOK_PARAMS_TEST: 'apw_49_facebook_params_test',
  AYAH_65_SD_SHOWTYPE_TEST: 'apw_65_ap_sd_313_showtype',
  AYAH_59_UPSELL_V2_TEST: 'apw_59_ap_quran_313_c_upsellv2',
  AYAH_83_AP_QURAN_313_C_ALTERNATIVE2_TEST:
    'apw_83_ap_quran_313_c_alternative2',
  AYAH_84_DYNAMIC_HEADLINES_TEST: 'apw_84_ap_quran_313_c_dynamicheadlines',
  AYAH_85_FLOW_AYAH_313_NEW_PRICE_V2_TEST: 'apw_85_flow_ayah_313_newprice_v2',
  AYAH_93_SD_QUIZ_VARIANT: 'apw_93_ap_sd_313_c_sdquiz',
  AYAH_94_LOCAL_PRICES_TEST: 'apw_94_ap_quran_313_c_localprices',
  AYAH_95_WLDB_FIRST_SCREEN_V2_TEST: 'apw_95_ap_wldb_313_1stscreenv2',
  AYAH_96_FINANCE_SCRATCH_TEST: 'apw_96_ap_finance_313_c_scratchv2',
  AYAH_98_AUDIO_UPSELL_V2_TEST: 'apw_98_ap_quran_313_c_audioupsell_v2',
  AYAH_99_FINANCE_FIRST_SCREEN_TEST: 'apw_99_ap_finance_313_c_1stscreen',
  AYAH_103_PARENTING_UPSELL_TEST: 'apw_103_ap_parenting_313_c_upsell',
  AYAH_105_NEW_PRICE_V2_TEST: 'apw_105_ap_quran_313_c_newpricev2',
  AYAH_107_WLDB_PAYWALL_TEST: 'apw_107_ap_wldb_paywall',
  AYAH_108_WLDB_QUIZ_VARIANT: 'apw_108_ap_wldb_quiz',
  AYAH_110_MONTHLY_UPSELL_TEST: 'apw_110_ap_monthly_ap',
  AYAH_112_QURAN_AGE_TEST: 'apw_112_ap_quran_313_c_age',
  AYAH_113_FLOW_AYAH_QUIZ_VARIANT: 'apw_113_flow_ayah_313_quiz',
  AYAH_115_PARENTING_QUIZ_V5: 'apw_115_ap_parenting_313_c_quizv5',
  AYAH_116_PARENTING_UPI_TEST: 'apw_116_ap_parenting_313_c_upi',
  AYAH_119_OPEN_QUESTION_TEST: 'apw_119_ap_quran_313_c_openq',
  AYAH_120_NEW_PRICING_TEST: 'apw_120_ap_quran_313_c_new_pricing',
  AYAH_123_DISCOUNT_TEST: 'apw_123_ap_quran_313_c_discount',
  AYAH_126_KNOWLEDGE_EXTENDED_VARIANT: 'apw_126_ap_knowledge_428_c_extended',
  AYAH_128_KNOWLEDGE_EMAIL_TEST: 'apw_128_ap_knowledge_428_c_email',
}

export const enum DiscountPercent {
  FIFTY = 50,
  SIXTY = 60,
  SIXTY_FIVE = 65,
}

export const MAIN_STRUGGLE = {
  STRESS_OR_ANXIETY: 'stress_or_anxiety',
  FEELING_ALONE: 'feeling_alone',
  SPIRITUAL_BURNOUT: 'spiritual_burnout',
  SCREEN_AND_TIME_WASTING_HABITS: 'screen_and_time_wasting_habits',
  OVERTHINKING_AND_GUILT: 'overthinking_and_guilt',
  LACK_OF_PURPOSE: 'lack_of_purpose',
  NOT_SURE: 'not_sure',
} as const

export const GOAL = {
  GROWING_CLOSER_TO_ALLAH: 'growing_closer_to_allah',
  GROWING_CLOSER_TO_GOD: 'growing_closer_to_god',
  FINDING_CLEARER_PURPOSE: 'finding_clearer_purpose',
  STRENGTHENING_FAITH: 'strengthening_faith',
  NURTURING_INNER_PEACE: 'nurturing_inner_peace',
  BUILDING_HEALTHIER_HABITS: 'building_healthier_habits',
} as const

export const W_TYPE_VARIANT = {
  TIGER: 'tiger',
  DRAGON: 'dragon',
  CRANE: 'crane',
  TURTLE: 'turtle',
} as const

export const ARCHETYPE = {
  MUHAMMAD: 'muhammad',
  MUSA: 'musa',
  IBRAHIM: 'ibrahim',
  YUSUF: 'yusuf',
  NUH: 'nuh',
  FATIMA: 'fatima',
  ASIYA: 'asiya',
  MARYAM: 'maryam',
  KHADIJAH: 'khadijah',
  AISHA: 'aisha',
} as const

export type TArchetype = TValueOf<typeof ARCHETYPE>

export const ARCHETYPE_LABEL: Record<TArchetype, string> = {
  [ARCHETYPE.MUHAMMAD]: 'commonComponents.archetype.muhammad',
  [ARCHETYPE.MUSA]: 'commonComponents.archetype.musa',
  [ARCHETYPE.IBRAHIM]: 'commonComponents.archetype.ibrahim',
  [ARCHETYPE.YUSUF]: 'commonComponents.archetype.yusuf',
  [ARCHETYPE.NUH]: 'commonComponents.archetype.nuh',
  [ARCHETYPE.FATIMA]: 'commonComponents.archetype.fatima',
  [ARCHETYPE.ASIYA]: 'commonComponents.archetype.asiya',
  [ARCHETYPE.MARYAM]: 'commonComponents.archetype.maryam',
  [ARCHETYPE.KHADIJAH]: 'commonComponents.archetype.khadijah',
  [ARCHETYPE.AISHA]: 'commonComponents.archetype.aisha',
}

export const PrintableType = {
  RAMADAN_PLANNER: 'ramadan_planner',
} as const
